<template>
  <!-- drag/drop.prevent keep the image from opening in the browser. May want to also do this on the parent -->
  <div @dragover.prevent @drop.prevent style="max-height: 100%; display: flex; flex-direction: column; justify-content: flex-start; align-items: flex-start">
    <div v-if="isOpen && singlePickMode && images?.length > 0" style="width: 100%">
      <ImageCropper :selectedImageServerId="cachedActiveServerId" @onCrop="uploadCroppedImage" />
    </div>
    <div style="width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: flex-start; align-items: flex-start">
      <p v-if="singlePickMode" class="note">Choose a photo to use.</p>
      <!-- Upload new and show photos section -->
      <div
        v-if="images"
        :class="{ uploadedContent: true, drag: isDragOverActive }"
        @drop="
          $event => {
            dragFile($event);
            isDragOverActive = false;
          }
        "
        :style="{
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          ...(isError && { boxShadow: '0 0 0 5px rgba(178, 34, 34, 0.27)' }),
        }"
        @dragenter="isDragOverActive = true"
        @dragleave="
          event => {
            const target = event.currentTarget;
            const relatedTarget = event.relatedTarget;
            if (!target || !relatedTarget || !target.contains(relatedTarget)) {
              isDragOverActive = false;
            }
          }
        "
        style="width: 100%; height: 100%; flex-direction: column; justify-content: flex-start; align-items: flex-start"
      >
        <input
          type="file"
          style="width: 100%; height: 100%; position: absolute; inset: 0 auto auto 0; opacity: 0; cursor: pointer"
          :multiple="!singlePickMode"
          :accept="fileInputAccept"
          ref="fileInput"
          @change="onImageReadyToUploadV2"
          :id="inputId"
        />
        <!-- Also repeated at the bottom of there are enough photos -->
        <div
          class="uploadBtn"
          :class="{ center: images?.length < 1 }"
          @click="clickUploadImage"
          style="width: 100%"
          :style="{
            height: images.length ? 'auto' : 'calc(100% - 10px)',
            padding: !images.length || !isThumbnailsVisible ? '40px 10px 30px' : '25px 10px 25px',
          }"
        >
          <div name="plus" size="small" />
          <span :style="{ color: `#${accentColor}`.replace('##', '#') }">
            {{ singlePickMode ? uploadPromptSingleLabel : uploadPromptMultiLabel }}
          </span>
          <div v-if="isUploadInProgress" class="uploadBtn__progressbar">
            <div class="uploadBtn__progressbar-indicator" :style="`width: ${uploadProgress}%`"></div>
            uploading<EllipsisAnimated />
          </div>
        </div>

        <div :class="{ transparentForClicks: isDragOverActive }">
          <slot name="thumbnailsList" :items="images" :removeItem="removeMediaFile" />
        </div>

        <ThumbnailsList
          v-if="isThumbnailsVisible && !Object.keys($slots).includes('thumbnailsList')"
          :images="images"
          :showRemove="showRemove"
          :singlePickMode="singlePickMode"
          :cachedActiveServerId="cachedActiveServerId"
          :isDragOverActive="isDragOverActive"
          :columnsNumber="columnsNumber"
          @removeMediaFile="index => removeMediaFile(index, true)"
          @imageClicked="item => imageClicked(item)"
          @reorderImages="(oldIndex, newIndex) => $emit('reorderImages', oldIndex, newIndex)"
          :isWithDragAndDrop="isWithDragAndDrop"
        />

        <!-- Also repeated at the top -->
        <label v-if="images.length > 10 && isThumbnailsVisible" class="uploadBtn" for="image" @click="clickUploadImage">
          <div name="plus" size="small" />
          <span :style="{ color: `#${accentColor}`.replace('##', '#') }">
            {{ singlePickMode ? uploadPromptSingleLabel : uploadPromptMultiLabel }}
          </span>
          <div v-if="isUploadInProgress" class="uploadBtn__progressbar">
            <div class="uploadBtn__progressbar-indicator" :style="`width: ${uploadProgress}%`"></div>
            uploading<EllipsisAnimated />
          </div>
        </label>
      </div>
      <div v-if="!hideVideoGuidance">Videos take a bit to process and clips under a minute work best.</div>
      <div v-if="showWarning" class="warning">Max {{ maxNumberOfFilesToUploadAtOnce }} uploads at a time.</div>
      <div v-if="showMinSizeOrResolutionWarning" :class="isCollab ? 'error' : 'warning'">
        Some images are under
        {{ validateBy === "size" ? `${isCollab ? minCollabFileSizeErrorInKB : minFileSizeWarningInKB}KB` : `${minFileResolutionWarningInPX}px*${minFileResolutionWarningInPX}px` }}, we recommend
        uploading higher quality images.
      </div>
      <div v-if="uploadError?.length > 0" class="error" data-tooltip="Questions or issues with uploads? Email support@shrpa.com">{{ uploadError }}</div>
      <div v-if="isCollab" style="margin-bottom: 5px">
        <b>Note:</b> Images must be at least {{ validateBy === "size" ? `${minCollabFileSizeErrorInKB}KB` : `${minCollabFileResolutionErrorInPX}px*${minCollabFileResolutionErrorInPX}px` }} for creator
        visits.
      </div>
      <SrpButton v-if="showClose" style="margin: 8px 0 1em !important; float: right; align-self: flex-end" @click="close" color="gray" size="small">Close</SrpButton>
    </div>

    <!-- Generic Message Modal -->
    <SrpModal v-model:isVisible="showMessageModal">
      <template #content>
        <div style="white-space: pre-wrap">
          <h4>{{ messageModalText }}</h4>
        </div>
      </template>
      <template #footer>
        <SrpButton @click="showMessageModal = false">Ok</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, inject, PropType } from "vue";
import axios from "axios";
import { BlockBlobClient } from "@azure/storage-blob";
import FileUtils, { FileMimeTypes } from "@logic/FileUtils";
import { getImageResolution, getVideoResolution } from "@helpers/GetImageOrVideoResolution";
import ImageBlobReduce from "image-blob-reduce";
import ImageUploader, { UploadFlow } from "@logic/ImageUploader";
import { useToastsStore } from "@stores/toasts";
import { MAX_CONCURRENT_UPLOADS_DEFAULT, ParallelAssetUploader } from "./ParallelAssetUploader";
import { mapState } from "pinia";
import { useUserProfileStore } from "@stores/userProfileStore";
import { checkIsNetworkErrorFromError, getErrorMessage } from "@/logic/ErrorHelper";
import { MetricSender } from "@/helpers/MetricSender";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { UploadedImage } from "@contracts/uploadedImage";

// Components
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import ImageCropper from "./ImageCropper.vue";
import LoaderComponent from "@components/Loader/Loader.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpMasonry from "@components/ui/SrpMasonry.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import VideoRender from "@components/VideoRender.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CloseDeleteButton from "@components/ui/CloseDeleteButton.vue";
import { getGlobalLink } from "@/logic/ExternalLinks";
import ThumbnailsList from "./ThumbnailsList.vue";

// NOTE! We now have two emit modes from this components.  The original one watches the images array and events any changes to it
// The newer Photo Gallery code events off each photo upload (since it updates the server)

export const MinCollabFileSizeErrorInKB = 500; // Dropped from 700 to 500, should probably use pixel dimensions instead
// Note: ImageUploads only retry on network error
const ImageUploadRetryCount = 5;
// Azure SDK has built-in retry logic so not doing as many retries on top of that.
const VideoUploadRetryCount = 3;
const TimeBetweenRetriesInMs = 4000;

export default defineComponent({
  name: "UploadPhotoForm",

  components: {
    ThumbnailsList,
    CloseDeleteButton,
    IconEmbedded,
    EllipsisAnimated,
    SrpFileThumbnail,
    ImageCropper,
    LoaderComponent,
    SrpButton,
    SrpMasonry,
    SrpModal,
    VideoRender,
  },

  props: {
    img: { type: Array as () => Array<any> | null, default: null },
    activeServerId: { type: String, required: false, default: "" },
    singlePickMode: { type: Boolean, default: false },
    showClose: { type: Boolean, default: false },
    autoSelect: { type: Boolean, default: false },
    isOpen: { type: Boolean, default: false },
    showRemove: { type: Boolean, default: false },
    isWithDragAndDrop: { type: Boolean, default: false },
    isCollab: { type: Boolean, default: false },
    collabImagesUsed: { type: Array as () => Array<string>, default: () => [] },
    collabImagesUnused: { type: Array as () => Array<string>, default: () => [] },
    isThumbnailsVisible: { type: Boolean, default: true },
    isError: { type: Boolean, default: false },
    columnsNumber: { type: Number, default: 0, required: false },
    accentColor: { type: String, default: "", required: false },
    hideVideoGuidance: { type: Boolean, default: false },
    isVideoUploadAllowed: { type: Boolean, default: true },
    isVideoOnlyUploadAllowed: { type: Boolean, default: false },
    callerName: { type: String, default: "notSet" },
    isFilesLimitReached: { type: Boolean, default: false, required: false },
    flow: { type: String as PropType<UploadFlow>, default: null },
    inputId: { type: String, default: "" },
    maxNumberOfFilesToUploadAtOnce: { type: Number, default: 150, required: false },
    beforeUploadCheck: { type: Function as PropType<(fileCount: number) => boolean>, default: null, required: false },
  },

  emits: {
    imageUploadedToServer: (uploadedImage: UploadedImage, autoSelect?: boolean, uncroppedImageServerId?: string) => true,
    close: () => true,
    removeMediaFile: (imageServerId: string) => true,
    uploadImagesChanged: () => true,
    uploadProgressChange: (info: { isUploadInProgress: boolean; uploadProgress: number }) => true,
    fileTooSmallError: (inputFileName: string) => true,
    invalidFileType: (inputFileName: string) => true,
    uploadingStarted: () => true,
    reorderImages: (oldIndex: number, newIndex: number) => true,
    error: () => true,
  },

  data() {
    return {
      globalLog: inject("globalLog") as any,
      globalRemoteLogger: inject("globalRemoteLogger") as any,
      screenSize: inject("screenSize") as ScreenSize,
      userProfileStore: useUserProfileStore(),

      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,

      validateBy: "size" as "size" | "resolution",

      minFileSizeWarningInKB: 250,
      minCollabFileSizeErrorInKB: MinCollabFileSizeErrorInKB,

      minFileResolutionWarningInPX: 1024,
      minCollabFileResolutionErrorInPX: 720,

      maxVideoSizeInMB: 600,

      isUploadInProgress: false,
      uploadProgress: 0,

      // Note: This is set server-side also in the config
      // Retired now that we client-side resize maxFileSizeInMB: 13,
      showWarning: false,
      showMinSizeOrResolutionWarning: false,
      uploadError: null as string | null,
      mainImage: null,
      showMessageModal: false,
      messageModalText: "Not set",

      isDragOverActive: false, // for the thumbnails to not block the drag&drop event
      cachedActiveServerId: this.activeServerId ?? this.img[0]?.serverId ?? "", // Used to keep track of the active image when in singlePickMode

      abortControllers: [],
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["getViewingUserProfile", "getActingUserProfile"]),
    images() {
      return this.img;
    },
    fileInputAccept(): string {
      if (this.isVideoOnlyUploadAllowed) {
        return "video/*";
      }
      if (this.isVideoUploadAllowed) {
        return "image/*,video/*";
      }
      return "image/*";
    },
    uploadPromptSingleLabel(): string {
      if (this.isVideoOnlyUploadAllowed) {
        return "Upload a new Video";
      }
      if (this.isVideoUploadAllowed) {
        return "Upload a new Photo or Video";
      }
      return "Upload a new Photo";
    },
    uploadPromptMultiLabel(): string {
      if (this.isVideoOnlyUploadAllowed) {
        return "Add or Drag in Videos";
      }
      if (this.isVideoUploadAllowed) {
        return "Add or Drag in Photos and Videos";
      }
      return "Add or Drag in Photos";
    },
  },

  watch: {
    activeServerId(value: string) {
      this.cachedActiveServerId = this.activeServerId;
    },
    uploadProgress() {
      this.$emit("uploadProgressChange", {
        isUploadInProgress: Boolean(this.isUploadInProgress),
        uploadProgress: this.uploadProgress,
      });
    },
    isFilesLimitReached() {
      if (this.isFilesLimitReached) {
        this.abortControllers.forEach(ac => ac?.abort());

        this.abortControllers = [];

        this.$emit("uploadProgressChange", {
          isUploadInProgress: false,
          uploadProgress: 100,
        });
      }
    },
  },

  methods: {
    async uploadCroppedImage(file: File) {
      var imageDetails = {
        name: file.name,
        size: file.size,
        serverId: null,
        finishedUploading: ref(false),
      } as UploadedImage;
      MetricSender.sendMetric("Upload", { type: "CroppedImage" });
      await this.uploadImage(imageDetails, file, true, this.cachedActiveServerId, ImageUploadRetryCount);
    },
    // NOTE! PhotoSpoiler calls this directly, so don't rename without checking that code
    dragFile(e) {
      if (this.isFilesLimitReached) {
        console.warn("File upload limit reached");
        useToastsStore().warn({
          message: "You've reached the upload limit for the trial",
          expiresInMs: 10000,
          details: "Paid plans can upload unlimited photos!",
        });
        return;
      }
      this.globalLog.info("Drag file");
      this.onImageReadyToUploadV2(e);
    },
    close() {
      this.showWarning = false;
      this.showMinSizeOrResolutionWarning = false;
      this.uploadError = null;
      this.$emit("close");
    },
    imageClicked(image: UploadedImage) {
      if (!this.singlePickMode) return;
      if (!image.serverId || image.serverId.length == 0) return;
      this.cachedActiveServerId = image.serverId;
    },
    clickUploadImage() {
      this.showWarning = false;
      this.uploadError = null;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.$refs.fileInput.click();
    },
    async onImageReadyToUploadV2(e) {
      if (!e?.target?.files?.length && !e?.dataTransfer?.files?.length) {
        // Note: This method only sometimes gets called if not files are selected
        // Ex. In Chrome desktop, it only does if you upload a file, then try to upload again but cancel
        this.globalRemoteLogger.info(`ImageUpload NoFilesChosen: user=${this.getActingUserProfile?.sherpaId}, collab=${this.isCollab}`, false, false, {
          creatorId: this.getActingUserProfile?.sherpaId,
          isCollab: this.isCollab,
        });
        return;
      }
      // V2 uses logic to only upload a certain number of files at a time
      // The previous logic could crash mobile browsers if trying to upload too many files.
      let assets: File[] = e.target?.files?.length ? Array.from(e.target.files) : Array.from(e.dataTransfer.files);
      if (assets.length > this.maxNumberOfFilesToUploadAtOnce) {
        this.showWarning = true;
        assets = assets.slice(0, this.maxNumberOfFilesToUploadAtOnce);
      }
      await this.uploadFiles(assets);
    },
    async uploadFiles(assets: File[], fromImageCropper = false) {
      // Filter out videos if not allowed; keep only videos when Reel / video-only mode
      const initialAssetCount = assets.length;
      if (!this.isVideoUploadAllowed) {
        assets = assets.filter(asset => !FileUtils.isVideoFileType(asset.name, asset.type));
      }
      if (this.isVideoOnlyUploadAllowed) {
        assets = assets.filter(asset => FileUtils.isVideoFileType(asset.name, asset.type));
      }
      const assetsToUploadCount = assets.length;

      // Call pre-upload validation callback if provided
      if (this.beforeUploadCheck && !this.beforeUploadCheck(assetsToUploadCount)) {
        // Validation failed, clear the file input and abort
        const fileInput = document.getElementById(this.inputId) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
        return;
      }

      this.$emit("uploadingStarted");
      // Set a session property so metrics know which flow this is
      MetricSender.setPropertyOnSession("UploadScenario", this.callerName);

      this.globalRemoteLogger.info(
        `UploadFiles: initialCount=${initialAssetCount}, videoAllowed=${this.isVideoUploadAllowed}, videoOnly=${this.isVideoOnlyUploadAllowed}, uploadingCount=${assetsToUploadCount}, caller=${this.callerName}`,
        false,
        false
      );

      var uploader = new ParallelAssetUploader();
      await uploader.uploadImagesAndVideos(
        assets,
        async (assetToUpload: File) => {
          if (this.isFilesLimitReached) {
            console.warn("File upload limit reached");
            useToastsStore().warn({
              message: "You've reached the upload limit for the trial",
              expiresInMs: 10000,
              details: "Paid plans can upload unlimited photos!",
            });
            return;
          }

          const ac = new AbortController();
          this.abortControllers.push(ac);

          // prettier-ignore
          try {
          let canBeUploadedResult = await this.inputFileCanBeUploaded(assetToUpload);
          const assetSizeInMB = assetToUpload.size / 1024 / 1024;
          this.globalRemoteLogger.info(
            `uploadingStarted name=${assetToUpload.name} sizeBeforeResize=${assetSizeInMB}MB, canBeUploaded=${canBeUploadedResult.isValid}, user=${this.getActingUserProfile?.sherpaId}, collab=${this.isCollab}`,
            false,
            false,
            {
              originalSizeInMB: assetSizeInMB,
              canUpload: canBeUploadedResult.isValid,
              reason: canBeUploadedResult.reason,
              creatorId: this.getActingUserProfile?.sherpaId,
              isCollab: this.isCollab,
            }
          );
          if (!canBeUploadedResult.isValid) {
            MetricSender.sendMetric("Upload-Warning", { reason: canBeUploadedResult.reason });
            return;
          }

          if (FileUtils.isVideoFileType(assetToUpload.name, assetToUpload.type)) {
            this.globalRemoteLogger.info("VideoUpload: " + assetToUpload.name);
            MetricSender.sendMetric("Upload", { type: "Video" });
            // @ts-ignore
            assetToUpload.finishedUploading = ref(false);

            await this.uploadVideo(assetToUpload, VideoUploadRetryCount, ac.signal);
          } else {
            MetricSender.sendMetric("Upload", { type: "Image" });
            // We're resizing client-side now before sending to the server.
            // This way we don't have a size limit and everything performs better.
            const resizeResult = await this.resizeImage(assetToUpload);
            if (!resizeResult.success) {
              // The method throws if truly unexpected so in this case just returning.
              return;
            }

            if (!(await this.validateFile(assetToUpload))) {
              this.showMinSizeOrResolutionWarning = true;
            }

            let resizedImage = resizeResult.blob;
            var imageDetails = {
              name: assetToUpload.name,
              size: assetToUpload.size,
              serverId: null,
              finishedUploading: ref(false),
            } as UploadedImage;

            await this.uploadImage(imageDetails, resizedImage, this.singlePickMode ? false : this.autoSelect, fromImageCropper ? this.cachedActiveServerId : null, ImageUploadRetryCount, ac.signal);
          }
          } catch (error) {
            if (error.name === "CanceledError" || axios.isCancel(error)) {
              console.info(`${assetToUpload.name} file upload canceled`);
            }
          }
        },
        MAX_CONCURRENT_UPLOADS_DEFAULT,
        (isUploadInProgress: boolean, progress: number) => {
          this.isUploadInProgress = isUploadInProgress;
          this.uploadProgress = progress;
        }
      );
    },
    async checkForHeicUpload(assetToUpload: File) {
      try {
        if ((await FileUtils.getFileMimeType(assetToUpload)) === FileMimeTypes.heic) {
          this.globalRemoteLogger.info(`UploadAsset Warn: Heic file mime type but jpeg extension - fileName=${assetToUpload?.name}, size=${assetToUpload?.size}`, true);
          useToastsStore().warn({
            message: `.heic files must be converted to jpeg format: ${assetToUpload?.name}`,
            expiresInMs: 10000,
            link: getGlobalLink("ConvertingHeicToJpeg"),
          });
          return true;
        }
        return false;
      } catch (error) {
        this.$emit("error");
        this.globalRemoteLogger.error(`checkForHeicUpload failed: ${error?.message ?? error} fileName=${assetToUpload?.name}, size=${assetToUpload?.size}, stack=${error?.stack}`, true);
        return false;
      }
    },
    async checkForFingerprintProtection(assetToUpload: File, clientError: any) {
      // Our image resizing fails if strict settings are enabled:
      // "Pica: cannot use getImageData on canvas, make sure fingerprinting protection isn't enabled"
      try {
        if (clientError?.message?.includes("fingerprinting protection")) {
          this.globalRemoteLogger.info(`UploadAsset Warn: Fingerprint protection detected - '${clientError?.message}' fileName=${assetToUpload?.name}, size=${assetToUpload?.size}`, true);
          useToastsStore().warn({
            message: `Browser settings are blocking image processing. Please relax strict mode and try again.`,
            expiresInMs: 10000,
          });
          return true;
        }
        return false;
      } catch (error) {
        this.$emit("error");
        this.globalRemoteLogger.error(`checkForFingerprintProtection failed: ${error?.message ?? error} fileName=${assetToUpload?.name}, size=${assetToUpload?.size}, stack=${error?.stack}`, true);
        return false;
      }
    },
    // Validates if the file should be uploaded and sets the uploadError message
    async inputFileCanBeUploaded(inputFile: File): Promise<{ isValid: boolean; reason: string | null }> {
      let fileSizeInKB = Math.floor(inputFile.size / 1024);
      let fileSizeInMB = Math.floor(fileSizeInKB / 1024);
      // File Type
      var error = FileUtils.isFileSupportedForUpload(inputFile.name, inputFile.type, inputFile.size, false);
      if (error?.length > 0) {
        const isHeic = inputFile.name?.toLowerCase().endsWith(".heic");
        const link = isHeic ? getGlobalLink("ConvertingHeicToJpeg") : null;
        useToastsStore().warn({ message: error, expiresInMs: 10000, link: link });
        // Show the error and skip this asset
        this.uploadError = error + (isHeic ? " See " + getGlobalLink("ConvertingHeicToJpeg") : "");
        this.globalRemoteLogger.info(`UploadAsset Warn: Invalid File: ${inputFile.type} ${inputFile.name} size=${fileSizeInKB}K, collab=${this.isCollab}`);
        // Note: Type is often empty, what we likely want is the extension (so passing the filename)
        this.$emit("invalidFileType", inputFile.name);
        return { isValid: false, reason: isHeic ? "HeicFile" : "otherInvalidType" };
      }
      // Min file size
      if (this.isCollab === true) {
        if (!(await this.validateFile(inputFile, true))) {
          // Note: Still uploading the file so we can troubleshoot
          var uploadedFileName = await ImageUploader.uploadItineraryImageBlob(inputFile, false, inputFile.name, this.flow);
          this.uploadError = `We couldn't upload some of your images because they were too small.`;
          // July 2024 Update: Removed showing the actual image and just show a toast with the file name
          useToastsStore().warn({ message: `${inputFile.name} is too small,\ncollab images must be > ${MinCollabFileSizeErrorInKB}KB` });
          this.globalRemoteLogger.info(
            `UploadAsset Warn: TooSmall for ${this.getActingUserProfile?.sherpaId}: ${fileSizeInKB}K ${uploadedFileName} ${inputFile.name} ${inputFile.type}, collab=${this.isCollab}`
          );
          this.$emit("fileTooSmallError", inputFile.name);
          return { isValid: false, reason: "tooSmall" };
        }
      }
      // Video max file size
      let isVideo = FileUtils.isVideoFileType(inputFile.name, inputFile.type);
      if (isVideo && inputFile.size > this.maxVideoSizeInMB * 1024 * 1024) {
        this.uploadError = `Videos must be less than ${this.maxVideoSizeInMB}MB`;
        this.globalRemoteLogger.info(`UploadAsset Warn: TooBig ${fileSizeInMB}M ${inputFile.type}, ${inputFile.name}, collab=${this.isCollab}`);
        return { isValid: false, reason: "videoTooBig" };
      }

      // Looks good
      return { isValid: true, reason: null };
    },
    async validateFile(file: File | UploadedImage, isStrict = false) {
      let isFileValid = false;

      const minSize = (isStrict ? this.minCollabFileSizeErrorInKB : this.minFileSizeWarningInKB) * 1024;
      const minResolution = isStrict ? this.minFileResolutionWarningInPX : this.minCollabFileResolutionErrorInPX;

      if (this.validateBy === "size") {
        isFileValid = file.size >= minSize;
      } else {
        const isVideo = FileUtils.isVideoFileType(file.name, file.type);
        const fileResolution = isVideo ? await getVideoResolution(file as File) : await getImageResolution(file as File);
        isFileValid = fileResolution.w >= minResolution || fileResolution.h >= minResolution;
      }

      return isFileValid;
    },
    async uploadVideo(file, retryCount: number, abortControllerSignal?: AbortSignal) {
      let videoId = null;
      let uploadState = "not-started";
      let start = performance.now();
      try {
        this.globalRemoteLogger.info(`ClientUploading Video ${file.name} ${file.size / 1024 / 1024}MB`);
        const isCommunityUploadFlow = this.flow === "community-upload";
        const videoUploadPath = isCommunityUploadFlow ? "videoupload/community-upload" : "videoupload";
        const flowValue = this.flow || "";
        const encodedFlow = encodeURIComponent(flowValue);
        // Get a token so we can upload the video directly to storage and then ask the services to process it.
        let slotUri = `${import.meta.env.VITE_API_URL}/${videoUploadPath}/slot?fileName=${encodeURIComponent(file.name)}&flow=${encodedFlow}`;
        const { data } = await axios.get(slotUri, { signal: abortControllerSignal });
        // use this to attach the video to the adventure
        videoId = data.videoId;
        // Use this for the file to upload to blob storage (more comments on the server)
        this.globalRemoteLogger.info(`VideoUpload Slot granted ${videoId} for file ${data.uploadFileName}`);
        uploadState = "slot-granted";

        // Set the serverId early so they can select the video even before it's done processing.
        // This should work fine unless they refresh the page before the video is done processing...
        file.serverId = videoId;

        // Upload the video to storage
        // Note: There are lots of retryOptions in StoragePipelineOptions but the defaults all seem reasonable.
        const blockBlobClient = new BlockBlobClient(data.authToken);
        this.globalRemoteLogger.info(`VideoUpload Starting. ${videoId} size=${file.size}`);
        // Uploads a browser Blob/File/ArrayBuffer/ArrayBufferView object to block blob. per https://learn.microsoft.com/en-us/javascript/api/@azure/storage-blob/blockblobclient?view=azure-node-latest
        await blockBlobClient.uploadData(file, { abortSignal: abortControllerSignal });
        uploadState = "uploadToAzure-complete";

        file.finishedUploading.value = true;

        let middle = performance.now();
        this.globalRemoteLogger.info(`VideoUpload complete, now processing... ${videoId}`);

        // Now request that the server process it.
        let processUri = `${import.meta.env.VITE_API_URL}/${videoUploadPath}/${data.uploadFileName}?flow=${encodedFlow}`;
        await axios.post(processUri, null, { signal: abortControllerSignal });
        uploadState = "uploadToShrpa-complete";
        let end = performance.now();
        // Update: Moved this to after the video uploads, which is the same way the photo upload works.
        this.$emit("imageUploadedToServer", file);
        this.globalRemoteLogger.info(`VideoUpload and Process Completed! ${videoId} UploadTime=${this.getSecondsDiff(start, middle)}, ProcessTime=${this.getSecondsDiff(middle, end)}`);
        MetricSender.sendMetric("Upload-Success", { type: "Video" });
      } catch (error: any) {
        this.$emit("error");
        // Note: Retrying even non-network errors for video since there are multiple calls and there may be different error messages
        if (retryCount > 0) {
          const triesLeft = retryCount - 1;
          MetricSender.sendMetric("Upload-Retry", { type: "Video" });
          this.globalRemoteLogger.info(`UploadRetry Video: ${getErrorMessage(error)}: runtime=${this.getRuntimeInMs(start)}ms for ${file?.name}, id=${videoId}, ${triesLeft} tries left`, true);
          // Wait a few seconds
          await new Promise(r => setTimeout(r, TimeBetweenRetriesInMs));
          // Retry
          return this.uploadVideo(file, triesLeft, abortControllerSignal);
        }
        // Used to track rate of users that hit any sort of upload error
        const isNetworkError = checkIsNetworkErrorFromError(error);
        MetricSender.sendMetric("Upload-Error", { fileType: "Video", isNetwork: isNetworkError });
        // Double log this for now (consider just having the global handler do it)
        this.globalRemoteLogger.error(`UploadError Video: ${getErrorMessage(error)}, uploadState=${uploadState}, id=${videoId}, fileName=${file?.name}, stack=${error?.stack}`, true);
        throw error;
      }
    },
    getSecondsDiff(startInMs: number, endInMs: number) {
      let diff = endInMs - startInMs;
      return Math.floor(diff * 1000);
    },
    calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
      let ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
      return { width: srcWidth * ratio, height: srcHeight * ratio };
    },
    async resizeImage(imageInput): Promise<{ blob: Blob | null; success: boolean }> {
      try {
        let maxDimension = 3200;
        let jpegQuality = 0.8;
        var fileType = imageInput.type;
        var inputBlob = new Blob([imageInput], { type: fileType || "application/*" });
        var reducer = new ImageBlobReduce();

        // Override to always output a jpeg at a certain quality https://github.com/nodeca/image-blob-reduce
        reducer._create_blob = function (env) {
          return this.pica.toBlob(env.out_canvas, "image/jpeg", jpegQuality).then(function (blob) {
            env.out_blob = blob;
            return env;
          });
        };

        let resizedImageBlob = await reducer.toBlob(inputBlob, { max: maxDimension });
        return { blob: resizedImageBlob, success: true };
      } catch (error) {
        this.$emit("error");
        // Note: Currently only checking for heic files on fail
        if (await this.checkForHeicUpload(imageInput)) {
          MetricSender.sendMetric("Upload-Warning", { reason: "HeicAsJpeg" });
          return { blob: null, success: false };
        }
        if (await this.checkForFingerprintProtection(imageInput, error)) {
          MetricSender.sendMetric("Upload-Warning", { reason: "FingerprintProtection" });
          return { blob: null, success: false };
        }
        // Used to track rate of users that hit any sort of upload error
        MetricSender.sendMetric("Upload-Error", { fileType: "Prep", isNetwork: false });
        // Double log this for now (consider just having the global handler do it)
        this.globalRemoteLogger.error(`UploadError PREP: ${getErrorMessage(error)}, fileName=${imageInput?.name}, size=${imageInput?.size}, stack=${error?.stack}`, true);
        try {
          // Upload the file as unprocessed so we can troubleshoot
          var uploadedFileName = await ImageUploader.uploadItineraryImageBlob(imageInput, false, imageInput.name, this.flow);
          this.globalRemoteLogger.info(`Unprocessed file success: uploadedFileName=${uploadedFileName}, fileName=${imageInput?.name}, size=${imageInput?.size}`, true);
        } catch (innerError) {
          this.$emit("error");
          this.globalRemoteLogger.warn(`Unprocessed file Failed: fileName=${imageInput?.name} ${innerError}`, true);
        }

        // Let the global handler process it
        throw error;
      }
    },
    async uploadImage(image: UploadedImage, rawFile, autoSelect: boolean, uncroppedImageServerId: string | null, retryCount: number, abortControllerSignal?: AbortSignal) {
      const startTime = performance.now();
      try {
        this.globalRemoteLogger.info(`ClientUploading Photo ${image.name} ${rawFile.size / 1024 / 1024}MB (was ${image.size / 1024 / 1024}MB) Autoselect=${autoSelect}`);

        var uploadedFileName = await ImageUploader.uploadItineraryImageBlob(rawFile, true, image.name, this.flow, abortControllerSignal);

        // await new Promise(r => setTimeout(r, 11000)); //Testing
        this.globalRemoteLogger.info(`Uploaded ${image.name} to server as ${uploadedFileName}`);

        if (uploadedFileName) {
          // Set the serverId and then updated the reactive property so it re-renders
          image.serverId = uploadedFileName;
          if (!image.finishedUploading) image.finishedUploading = ref(false);
          image.finishedUploading.value = true;
          // Note: uncroppedImageServerId is only used for the cover photos selection in SummaryForm.vue
          this.globalRemoteLogger.info(
            `UploadPhotoForm.upload: completed runtime=${this.getRuntimeInMs(startTime)}ms for ${image.name}, isCollab=${this.isCollab}, autoSelect=${autoSelect}, uncroppedImageServerId=${uncroppedImageServerId}`
          );
          if (this.autoSelect) this.cachedActiveServerId = image.serverId;
          this.$emit("imageUploadedToServer", image, autoSelect, uncroppedImageServerId);
          MetricSender.sendMetric("Upload-Success", { type: "Image" });
        }
      } catch (error: any | AxiosError) {
        this.$emit("error");
        // NOTE: Similar logic in uploadVideo()
        // Adding a retry in the case of a network error
        const isNetworkError = checkIsNetworkErrorFromError(error);
        if (retryCount > 0 && isNetworkError) {
          const triesLeft = retryCount - 1;
          MetricSender.sendMetric("Upload-Retry", { type: "Image" });
          this.globalRemoteLogger.info(`UploadRetry Photo: ${error.message}: runtime=${this.getRuntimeInMs(startTime)}ms for ${image?.name}, ${triesLeft} tries left`, true);
          // Wait a few seconds
          await new Promise(r => setTimeout(r, TimeBetweenRetriesInMs));
          // Retry
          return await this.uploadImage(image, rawFile, autoSelect, uncroppedImageServerId, triesLeft, abortControllerSignal);
        } else {
          // Used to track rate of users that hit any sort of upload error
          MetricSender.sendMetric("Upload-Error", { fileType: "Image", isNetwork: isNetworkError });
          // Double log this for now (consider just having the global handler do it)
          const networkTest = await this.checkInternetConnection();
          this.globalRemoteLogger.error(
            `UploadError Photo: ${getErrorMessage(error)}, runtime=${this.getRuntimeInMs(startTime)}ms for ${image?.name}, ${networkTest}, isCollab=${this.isCollab}, stack=${error?.stack}`,
            true
          );

          throw error;
        }
      }
    },
    getRuntimeInMs(startTime: number) {
      return Math.floor(performance.now() - startTime);
    },
    async checkInternetConnection(): Promise<string> {
      // This gets called when we have issues uploading images, so checking other endpoints/sites to help troubleshoot.
      let backendApiPingResult = null as string | null;
      // let frontendPingResult = null as string | null;
      // let externalPingResult = null as string | null;
      try {
        // Backend api ping
        const { data } = await axios.get(`${import.meta.env.VITE_API_URL}/ping`);
        backendApiPingResult = data;
      } catch (error) {
        this.$emit("error");
        backendApiPingResult = `Error: ${error?.message ?? error}`;
      }
      return `ExternalPing: deprecated, BackendApiPing: ${backendApiPingResult}`;
    },
    removeMediaFile(index: number, isValidImage: boolean) {
      // this.globalLog.info("Removing image at " + index);
      if (isValidImage) {
        let image = this.images[index];
        if (image.serverId) {
          this.$emit("removeMediaFile", image.serverId);
        }
      }
    },
    isVideoFileType(fileName: string) {
      return FileUtils.isVideoFileType(fileName, null);
    },
    showModalMessage(message) {
      this.messageModalText = message;
      this.showMessageModal = true;
    },
  },
});

// Note: In Options API, all methods are automatically exposed and accessible via template refs
</script>

<style scoped lang="scss">
.transparentForClicks {
  pointer-events: none;
}
.note {
  margin: 10px 0;
}

.uploadedContent {
  min-height: 110px;
  padding: 0 10px 0 5px;
  margin-bottom: 8px;
  border: 1px dashed;
  border-radius: 10px;
  overflow: auto;
  flex-direction: column;
}

.uploadedContent.drag {
  border: 3px dashed rgba(5, 133, 135, 0.8);
  background: rgba(5, 133, 135, 0.05);
}

.uploadBtn {
  padding: 16px;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #058587;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;

  &__progressbar {
    width: 150px;
    height: 7px;
    margin-top: 5px;
    border-radius: 100px;
    position: relative;
    color: rgba(91, 91, 91, 1);
    font: 14px/14px sans-serif;
    text-align: center;
    background: rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }

  &__progressbar-indicator {
    height: 7px;
    margin-bottom: 2px;
    border-radius: 1000px;
    inset: 0 auto auto 0;
    background: rgba(17, 134, 137, 1);
    transition: width 0.3s ease-in-out;
  }
}

.close {
  z-index: 20;
  cursor: pointer;
  position: absolute;
  right: 7px;
  top: 10px;
}

.spinner {
  z-index: 20;
  position: absolute;
  right: 50px;
  top: 40px;
}

.error {
  color: rgb(155, 0, 0);
  font-weight: bold;
}
.warning {
  color: rgb(255, 140, 0);
  font-weight: bold;
}

@media only screen and (max-width: 767px) {
  .uploadedContent {
    // max-height: 460px;
    overflow: auto;
  }
}
</style>
